import clsx from 'clsx';
import React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Description,
    EmptyState,
    Heading,
    LoadingIndicator,
    Pill,
    PillStack,
} from '@gitbook/spine-react';
import { useResizeObserver } from '@gitbook/util-react-hooks';

import {
    type AgentDockStarter,
    type AgentDockWelcome as AgentDockWelcomeData,
    type AgentDockWelcomeContext,
    type AgentDockWelcomePlace,
    getAgentDockWelcome,
} from './welcome';

/**
 * How much room the welcome needs to introduce the agent as well as offer the starters: what the
 * whole thing measures at the width of the dock's window — a logo, a name, a description over two
 * lines and four starters, inside the empty state's padding.
 *
 * The introduction takes about as much of that as the starters do, so in a window dragged short it
 * pushes them off the top of the conversation, where nothing can scroll back to them. Under this
 * height the starters are all the welcome shows.
 */
const WELCOME_HEADER_MIN_HEIGHT = 316;

/**
 * What an empty conversation shows in place of messages: who the user is talking to, what it can do
 * where they are, and a few ways to start.
 *
 * Read from where the user is rather than fixed, so the offer on a change request is about reviewing
 * and editing it, and the one on the home screen about finding things across the organization. The
 * chat gives it up as soon as the composer holds a draft, so it never competes with what the user is
 * about to say.
 */
export function AgentDockWelcome(props: {
    context: AgentDockWelcomeContext;
    /** A starter was picked. The place says what "this" meant in it. */
    onPickStarter: (pick: { starter: AgentDockStarter; place: AgentDockWelcomePlace }) => void;
}) {
    const { context, onPickStarter } = props;
    const welcome = getAgentDockWelcome(context);
    // None while what it offers is still loading, like the site's setup.
    return welcome ? (
        <AgentDockWelcomeContent welcome={welcome} onPickStarter={onPickStarter} />
    ) : (
        <LoadingIndicator className="m-auto text-muted" />
    );
}

function AgentDockWelcomeContent(props: {
    welcome: AgentDockWelcomeData;
    onPickStarter: (pick: { starter: AgentDockStarter; place: AgentDockWelcomePlace }) => void;
}) {
    const { welcome, onPickStarter } = props;
    const { place, title, description, starters } = welcome;

    // The room the conversation leaves for the welcome, which the user sets by dragging the top
    // edge of the dock's window. Measured on the border box, which is the room itself: a
    // measurement that moved with the padding of the layout it picks would flip between the two.
    const [room, setRoom] = React.useState<number | null>(null);
    const measureRoom = useResizeObserver((entry) => {
        const height = (entry.target as HTMLElement).offsetHeight;
        setRoom((previous) => (previous === height ? previous : height));
    });
    // Introduced until measured otherwise: the window opens tall enough for it, and starting from
    // the short layout would flash the logo in on every open.
    const withHeader = room === null || room >= WELCOME_HEADER_MIN_HEIGHT;

    const pills = (
        // Set off from the description above it, on top of the gap the empty state already keeps.
        <PillStack className={clsx('justify-center', withHeader && 'mt-2')}>
            {starters.map((starter) => (
                <Pill
                    key={starter.id}
                    label={starter.label}
                    leadingIcon={starter.icon}
                    onPress={() => onPickStarter({ starter, place })}
                />
            ))}
        </PillStack>
    );

    return (
        // Measured here rather than on the layouts below: this box is the room, and it keeps the
        // same geometry whichever of them fills it.
        <div ref={measureRoom} className="flex min-h-0 flex-1 flex-col justify-end">
            {withHeader ? (
                <EmptyState data-testid="agentDock.welcome" className="min-h-0 flex-1">
                    <Icon.AIAgentAnimated
                        state="intro"
                        size={32}
                        aria-hidden="true"
                        role="presentation"
                        className="mb-1 shrink-0 text-base"
                    />
                    <Heading>{title}</Heading>
                    <Description>{description}</Description>
                    {pills}
                </EmptyState>
            ) : (
                // Against the composer rather than centered in what is left: the starters are what
                // the user is about to say, and there is no title above them to sit under.
                <div data-testid="agentDock.welcome" className="p-3">
                    {pills}
                </div>
            )}
        </div>
    );
}
